/**
 * @micarahr/permissions
 *
 * Role and permission contracts for the whole MicaraHR platform.
 *
 * The employee mobile app only activates a small slice of this map — the
 * employee, team leader, manager and department head roles. Everything else is
 * declared now so the future HR & Administration web app and the future backend
 * enforce exactly the same vocabulary.
 *
 * Lesson carried into this build: a guard nobody has fired is not a guard. The
 * mobile app calls `can()` at every gated surface (see `useAuthorization`), and
 * the manager routes are unreachable without `approval.view`.
 */
import type { UserRole } from '@micarahr/shared-types';

export type { UserRole };

/**
 * Every permission the platform will eventually enforce. Grouped by module so
 * the future admin UI can render them without a second mapping.
 */
export const PERMISSIONS = {
  // Self-service — available to every active employee.
  SELF_PROFILE_VIEW: 'self.profile.view',
  SELF_PROFILE_EDIT: 'self.profile.edit',
  SELF_ATTENDANCE_RECORD: 'self.attendance.record',
  SELF_ATTENDANCE_VIEW: 'self.attendance.view',
  SELF_CORRECTION_SUBMIT: 'self.correction.submit',
  SELF_SCHEDULE_VIEW: 'self.schedule.view',
  SELF_SCHEDULE_CHANGE_REQUEST: 'self.schedule.change_request',
  SELF_LEAVE_SUBMIT: 'self.leave.submit',
  SELF_OVERTIME_SUBMIT: 'self.overtime.submit',
  SELF_PAYSLIP_VIEW: 'self.payslip.view',
  SELF_DOCUMENT_VIEW: 'self.document.view',
  SELF_ANNOUNCEMENT_VIEW: 'self.announcement.view',
  SELF_NOTIFICATION_VIEW: 'self.notification.view',
  SELF_DEVICE_REGISTER: 'self.device.register',

  // Manager surface — mobile approvals and team visibility.
  TEAM_VIEW: 'team.view',
  TEAM_ATTENDANCE_VIEW: 'team.attendance.view',
  TEAM_SCHEDULE_VIEW: 'team.schedule.view',
  TEAM_LEAVE_CALENDAR_VIEW: 'team.leave_calendar.view',
  APPROVAL_VIEW: 'approval.view',
  APPROVAL_DECIDE: 'approval.decide',
  APPROVAL_RETURN: 'approval.return',
  APPROVAL_DELEGATE: 'approval.delegate',

  // Department head — a wider team scope than a line manager.
  DEPARTMENT_VIEW: 'department.view',
  DEPARTMENT_REPORT_VIEW: 'department.report.view',

  // Future HR & Administration web app.
  EMPLOYEE_MANAGE: 'employee.manage',
  EMPLOYEE_VIEW_ALL: 'employee.view_all',
  ORGANIZATION_MANAGE: 'organization.manage',
  WORK_LOCATION_MANAGE: 'work_location.manage',
  GEOFENCE_MANAGE: 'geofence.manage',
  ATTENDANCE_POLICY_MANAGE: 'attendance_policy.manage',
  ATTENDANCE_REVIEW: 'attendance.review',
  ATTENDANCE_CORRECTION_APPROVE: 'attendance.correction.approve',
  SHIFT_MANAGE: 'shift.manage',
  SCHEDULE_ASSIGN: 'schedule.assign',
  LEAVE_BALANCE_MANAGE: 'leave.balance.manage',
  LEAVE_TYPE_MANAGE: 'leave.type.manage',
  LEAVE_APPROVE_HR: 'leave.approve_hr',
  OVERTIME_APPROVE_HR: 'overtime.approve_hr',
  PAYROLL_RUN: 'payroll.run',
  PAYROLL_APPROVE: 'payroll.approve',
  PAYSLIP_PUBLISH: 'payslip.publish',
  STATUTORY_TABLE_MANAGE: 'statutory_table.manage',
  ANNOUNCEMENT_PUBLISH: 'announcement.publish',
  POLICY_PUBLISH: 'policy.publish',
  DOCUMENT_MANAGE: 'document.manage',
  RECRUITMENT_MANAGE: 'recruitment.manage',
  LEARNING_MANAGE: 'learning.manage',
  ASSET_MANAGE: 'asset.manage',
  REPORT_VIEW: 'report.view',
  AUDIT_LOG_VIEW: 'audit_log.view',
  SYSTEM_ADMINISTER: 'system.administer',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

const SELF_SERVICE: Permission[] = [
  PERMISSIONS.SELF_PROFILE_VIEW,
  PERMISSIONS.SELF_PROFILE_EDIT,
  PERMISSIONS.SELF_ATTENDANCE_RECORD,
  PERMISSIONS.SELF_ATTENDANCE_VIEW,
  PERMISSIONS.SELF_CORRECTION_SUBMIT,
  PERMISSIONS.SELF_SCHEDULE_VIEW,
  PERMISSIONS.SELF_SCHEDULE_CHANGE_REQUEST,
  PERMISSIONS.SELF_LEAVE_SUBMIT,
  PERMISSIONS.SELF_OVERTIME_SUBMIT,
  PERMISSIONS.SELF_PAYSLIP_VIEW,
  PERMISSIONS.SELF_DOCUMENT_VIEW,
  PERMISSIONS.SELF_ANNOUNCEMENT_VIEW,
  PERMISSIONS.SELF_NOTIFICATION_VIEW,
  PERMISSIONS.SELF_DEVICE_REGISTER,
];

const TEAM_LEADER: Permission[] = [
  PERMISSIONS.TEAM_VIEW,
  PERMISSIONS.TEAM_ATTENDANCE_VIEW,
  PERMISSIONS.TEAM_SCHEDULE_VIEW,
  PERMISSIONS.APPROVAL_VIEW,
];

const MANAGER: Permission[] = [
  ...TEAM_LEADER,
  PERMISSIONS.TEAM_LEAVE_CALENDAR_VIEW,
  PERMISSIONS.APPROVAL_DECIDE,
  PERMISSIONS.APPROVAL_RETURN,
];

const DEPARTMENT_HEAD: Permission[] = [
  ...MANAGER,
  PERMISSIONS.APPROVAL_DELEGATE,
  PERMISSIONS.DEPARTMENT_VIEW,
  PERMISSIONS.DEPARTMENT_REPORT_VIEW,
];

const HR_OFFICER: Permission[] = [
  PERMISSIONS.EMPLOYEE_VIEW_ALL,
  PERMISSIONS.ATTENDANCE_REVIEW,
  PERMISSIONS.ATTENDANCE_CORRECTION_APPROVE,
  PERMISSIONS.SCHEDULE_ASSIGN,
  PERMISSIONS.LEAVE_APPROVE_HR,
  PERMISSIONS.OVERTIME_APPROVE_HR,
  PERMISSIONS.ANNOUNCEMENT_PUBLISH,
  PERMISSIONS.DOCUMENT_MANAGE,
  PERMISSIONS.REPORT_VIEW,
];

const HR_ADMINISTRATOR: Permission[] = [
  ...HR_OFFICER,
  PERMISSIONS.EMPLOYEE_MANAGE,
  PERMISSIONS.WORK_LOCATION_MANAGE,
  PERMISSIONS.GEOFENCE_MANAGE,
  PERMISSIONS.ATTENDANCE_POLICY_MANAGE,
  PERMISSIONS.SHIFT_MANAGE,
  PERMISSIONS.LEAVE_BALANCE_MANAGE,
  PERMISSIONS.LEAVE_TYPE_MANAGE,
  PERMISSIONS.POLICY_PUBLISH,
];

const PAYROLL_OFFICER: Permission[] = [
  PERMISSIONS.EMPLOYEE_VIEW_ALL,
  PERMISSIONS.PAYROLL_RUN,
  PERMISSIONS.REPORT_VIEW,
];

const PAYROLL_ADMINISTRATOR: Permission[] = [
  ...PAYROLL_OFFICER,
  PERMISSIONS.PAYROLL_APPROVE,
  PERMISSIONS.PAYSLIP_PUBLISH,
  PERMISSIONS.STATUTORY_TABLE_MANAGE,
];

const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

/**
 * Role → permission map. Every role also receives the self-service permissions
 * because managers, HR officers and administrators are employees too.
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  employee: [...SELF_SERVICE],
  team_leader: [...SELF_SERVICE, ...TEAM_LEADER],
  manager: [...SELF_SERVICE, ...MANAGER],
  department_head: [...SELF_SERVICE, ...DEPARTMENT_HEAD],
  hr_officer: [...SELF_SERVICE, ...HR_OFFICER],
  hr_administrator: [...SELF_SERVICE, ...HR_ADMINISTRATOR],
  payroll_officer: [...SELF_SERVICE, ...PAYROLL_OFFICER],
  payroll_administrator: [...SELF_SERVICE, ...PAYROLL_ADMINISTRATOR],
  recruiter: [...SELF_SERVICE, PERMISSIONS.RECRUITMENT_MANAGE, PERMISSIONS.EMPLOYEE_VIEW_ALL],
  learning_administrator: [...SELF_SERVICE, PERMISSIONS.LEARNING_MANAGE, PERMISSIONS.REPORT_VIEW],
  asset_custodian: [...SELF_SERVICE, PERMISSIONS.ASSET_MANAGE],
  auditor: [
    ...SELF_SERVICE,
    PERMISSIONS.AUDIT_LOG_VIEW,
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.EMPLOYEE_VIEW_ALL,
  ],
  company_administrator: [
    ...SELF_SERVICE,
    ...HR_ADMINISTRATOR,
    ...PAYROLL_ADMINISTRATOR,
    PERMISSIONS.ORGANIZATION_MANAGE,
    PERMISSIONS.AUDIT_LOG_VIEW,
    // Administering the company's users, roles and settings is what the role
    // is named for. Without this the role cannot reach System Users, Roles or
    // Company Settings, which leaves only the super administrator able to
    // grant anyone anything.
    PERMISSIONS.SYSTEM_ADMINISTER,
  ],
  super_administrator: ALL_PERMISSIONS,
};

/** Roles that the employee mobile app supports signing in with. */
export const MOBILE_SUPPORTED_ROLES: UserRole[] = [
  'employee',
  'team_leader',
  'manager',
  'department_head',
];

/** Roles that unlock the manager surface inside the mobile app. */
export const MOBILE_MANAGER_ROLES: UserRole[] = ['team_leader', 'manager', 'department_head'];

/**
 * Permissions that exist in the contract but are deliberately NOT honoured by
 * the mobile app. Even if a role carries them, the mobile app must not expose
 * HR, payroll, recruitment or system administration functionality (§6).
 */
export const MOBILE_EXCLUDED_PERMISSIONS: Permission[] = [
  PERMISSIONS.EMPLOYEE_MANAGE,
  PERMISSIONS.ORGANIZATION_MANAGE,
  PERMISSIONS.WORK_LOCATION_MANAGE,
  PERMISSIONS.GEOFENCE_MANAGE,
  PERMISSIONS.ATTENDANCE_POLICY_MANAGE,
  PERMISSIONS.ATTENDANCE_REVIEW,
  PERMISSIONS.SHIFT_MANAGE,
  PERMISSIONS.SCHEDULE_ASSIGN,
  PERMISSIONS.LEAVE_BALANCE_MANAGE,
  PERMISSIONS.LEAVE_TYPE_MANAGE,
  PERMISSIONS.LEAVE_APPROVE_HR,
  PERMISSIONS.OVERTIME_APPROVE_HR,
  PERMISSIONS.PAYROLL_RUN,
  PERMISSIONS.PAYROLL_APPROVE,
  PERMISSIONS.PAYSLIP_PUBLISH,
  PERMISSIONS.STATUTORY_TABLE_MANAGE,
  PERMISSIONS.ANNOUNCEMENT_PUBLISH,
  PERMISSIONS.POLICY_PUBLISH,
  PERMISSIONS.DOCUMENT_MANAGE,
  PERMISSIONS.RECRUITMENT_MANAGE,
  PERMISSIONS.LEARNING_MANAGE,
  PERMISSIONS.ASSET_MANAGE,
  PERMISSIONS.AUDIT_LOG_VIEW,
  PERMISSIONS.SYSTEM_ADMINISTER,
  PERMISSIONS.EMPLOYEE_VIEW_ALL,
];

/**
 * Permissions the mobile app honours.
 *
 * An allowlist, not a denylist. `MOBILE_EXCLUDED_PERMISSIONS` below records the
 * same boundary from the other side and is kept for documentation, but the
 * resolver reads *this* list — because every permission added for the HR web
 * app would otherwise have to be remembered in the exclusion list, and the one
 * that gets forgotten is the one that puts payroll on someone's phone.
 */
export const MOBILE_HONOURED_PERMISSIONS: Permission[] = [...SELF_SERVICE, ...DEPARTMENT_HEAD];

export interface PermissionSubject {
  roles: UserRole[];
}

/** Resolve the full permission set for a set of roles. */
export function permissionsForRoles(roles: UserRole[]): Permission[] {
  const set = new Set<Permission>();
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role] ?? []) {
      set.add(permission);
    }
  }
  return [...set];
}

/**
 * Resolve the permissions the *mobile app* honours. HR, payroll, recruitment
 * and administration permissions are stripped even when the role carries them,
 * because those surfaces belong to the future HR & Administration web app.
 */
export function mobilePermissionsForRoles(roles: UserRole[]): Permission[] {
  const honoured = new Set<Permission>(MOBILE_HONOURED_PERMISSIONS);
  return permissionsForRoles(roles).filter((permission) => honoured.has(permission));
}

export function can(subject: PermissionSubject, permission: Permission): boolean {
  return mobilePermissionsForRoles(subject.roles).includes(permission);
}

export function canAny(subject: PermissionSubject, permissions: Permission[]): boolean {
  const granted = new Set(mobilePermissionsForRoles(subject.roles));
  return permissions.some((permission) => granted.has(permission));
}

export function canAll(subject: PermissionSubject, permissions: Permission[]): boolean {
  const granted = new Set(mobilePermissionsForRoles(subject.roles));
  return permissions.every((permission) => granted.has(permission));
}

/** True when the account should see the manager surface in the mobile app. */
export function hasManagerSurface(roles: UserRole[]): boolean {
  return canAny({ roles }, [PERMISSIONS.APPROVAL_VIEW, PERMISSIONS.TEAM_VIEW]);
}

/** True when the account may actually decide on approvals (not just view). */
export function canDecideApprovals(roles: UserRole[]): boolean {
  return can({ roles }, PERMISSIONS.APPROVAL_DECIDE);
}

/** The role shown in the UI — the highest-privilege mobile role held. */
export function primaryMobileRole(roles: UserRole[]): UserRole {
  const order: UserRole[] = ['department_head', 'manager', 'team_leader', 'employee'];
  return order.find((role) => roles.includes(role)) ?? 'employee';
}
